import React, { useState, useEffect, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { Order, OrderItem, OrderStatus } from '../../../types/order';
import { Client } from '../../../types/client';
import { OrderItemRow } from './OrderItemRow';
import { PricingBreakdown } from './PricingBreakdown';
import { createOrderItem, finalizeOrderWithInventoryCheck } from '../../../store/slices/ordersSlice';
import { getDaysSinceOrder, getStatusColor, getOrderRowBackgroundColor } from '../../../utils/orderUtils';

const STATUSES: OrderStatus[] = ['Pending', 'In Progress', 'Completed'];

interface OrderCardProps {
  order: Order;
  recipes: { id: string; name: string }[];
  onUpdate: (order: Order) => void;
  onDelete: (id: string) => void;
  isHighlighted?: boolean;
  forceExpanded?: boolean;
  onToggle?: () => void;
}

const OrderCardComponent = forwardRef<HTMLDivElement, OrderCardProps>(
  ({ order, recipes, onUpdate, onDelete, isHighlighted = false, forceExpanded = false, onToggle }, ref) => {
    const dispatch = useAppDispatch();
    const [expanded, setExpanded] = useState(false);
    const [items, setItems] = useState<OrderItem[]>(order.items);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // Helper function to render customer type badge
    const renderCustomerTypeBadge = () => {
      // Handle case sensitivity: Orders store 'Individual'/'Retailer'
      const customerType = order.customerType;
      const isRetailer = customerType?.toLowerCase() === 'retailer';

      return (
        <span
          style={{
            fontSize: '0.7rem',
            padding: '3px 8px',
            backgroundColor: isRetailer ? '#9C27B0' : '#FF9800',
            color: 'white',
            borderRadius: '4px',
            fontWeight: 'bold'
          }}
        >
          {isRetailer ? 'RETAILER' : 'INDIVIDUAL'}
        </span>
      );
    };

    // Get clients from Redux to show linked client info
    const clients = useAppSelector(state => state.clients.clients);
    const linkedClient = order.clientId ? clients.find((c: Client) => c.id === order.clientId) : null;

    // Handle force expanded from parent
    useEffect(() => {
      if (forceExpanded) {
        setExpanded(true);
      }
    }, [forceExpanded]);

    const daysSince = getDaysSinceOrder(order.orderDate);

    const handleFieldChange = (field: keyof Order, value: any) => {
      onUpdate({ ...order, [field]: value });
    };

    const handleItemChange = (index: number, updatedItem: OrderItem) => {
      const newItems = [...items];
      newItems[index] = updatedItem;
      setItems(newItems);
      onUpdate({ ...order, items: newItems });
    };

    const handleRemoveItem = (index: number) => {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      onUpdate({ ...order, items: newItems });
    };

    const handleAddItem = () => {
      const newItem = createOrderItem();
      const newItems = [...items, newItem];
      setItems(newItems);
      onUpdate({ ...order, items: newItems });
    };

    const validateOrder = (): string[] => {
      const errors: string[] = [];

      if (!order.customerName.trim()) {
        errors.push('Customer name is required');
      }

      if (items.length === 0) {
        errors.push('At least one order item is required');
      }

      // Check if all items have a recipe selected
      const hasEmptyItems = items.some(item => !item.recipeId);
      if (hasEmptyItems) {
        errors.push('All order items must have a recipe selected');
      }

      return errors;
    };

    const handleFinalizeOrder = () => {
      const errors = validateOrder();

      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }

      setValidationErrors([]);

      // Update the order with current items before finalizing
      onUpdate({ ...order, items });

      // Use the thunk to finalize with inventory check and task generation
      dispatch(finalizeOrderWithInventoryCheck({ orderId: order.id }));

      setExpanded(false);
    };

    // Color styles based on order age
    const getAgeColor = () => {
      if (order.status === 'Completed') return '#9e9e9e';
      if (daysSince <= 1) return '#4CAF50'; // Green
      if (daysSince <= 3) return '#8BC34A'; // Yellow-Green
      if (daysSince <= 5) return '#FFC107'; // Yellow
      if (daysSince === 6) return '#FF9800'; // Orange
      return '#F44336'; // Red (7+)
    };

    return (
      <div
        ref={ref}
        style={{
          border: `2px solid ${isHighlighted ? '#2196F3' : '#ddd'}`,
          borderRadius: '8px',
          marginBottom: '16px',
          backgroundColor: getOrderRowBackgroundColor(daysSince, order.status),
          overflow: 'hidden',
          boxShadow: isHighlighted ? '0 0 20px rgba(33, 150, 243, 0.5)' : 'none',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        {/* Header */}
        <div
          onClick={() => {
            if (onToggle) {
              onToggle();
            } else {
              setExpanded(!expanded);
            }
          }}
          style={{
            padding: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: expanded ? '1px solid #ddd' : 'none'
          }}
        >
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {order.clientId ? (
                <Link
                  to={`/clients?open=${order.clientId}`}
                  onClick={e => e.stopPropagation()}
                  style={{
                    color: '#2196F3',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#1976D2')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#2196F3')}
                >
                  {order.customerName || 'New Order'}
                </Link>
              ) : (
                <span>{order.customerName || 'New Order'}</span>
              )}
              {renderCustomerTypeBadge()}
              {!order.isFinalized && (
                <span
                  style={{
                    fontSize: '0.75rem',
                    padding: '4px 8px',
                    backgroundColor: '#FFE082',
                    color: '#F57C00',
                    borderRadius: '4px',
                    fontWeight: 'normal'
                  }}
                >
                  DRAFT
                </span>
              )}
              {order.isReady && (
                <span
                  style={{
                    fontSize: '0.75rem',
                    padding: '4px 8px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}
                >
                  READY
                </span>
              )}
            </h3>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              <span>Order Date: {order.orderDate}</span>
              <span style={{ margin: '0 12px' }}>•</span>
              <span
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: getAgeColor(),
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                {daysSince} day{daysSince !== 1 ? 's' : ''} old
              </span>
              <span style={{ margin: '0 12px' }}>•</span>
              <span>
                {items.length} item{items.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <select
              value={order.status}
              onChange={e => {
                e.stopPropagation();
                handleFieldChange('status', e.target.value);
              }}
              onClick={e => e.stopPropagation()}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '2px solid',
                borderColor: getStatusColor(order.status),
                color: getStatusColor(order.status),
                fontWeight: 'bold',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {STATUSES.map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <span style={{ fontSize: '1.5rem', color: '#666' }}>
              {(onToggle ? forceExpanded : expanded) ? '▼' : '▶'}
            </span>
          </div>
        </div>

        {/* Expanded Content */}
        {(onToggle ? forceExpanded : expanded) && (
          <div style={{ padding: '20px' }}>
            {/* Linked Client Indicator */}
            {linkedClient && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196F3',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>🔗</span>
                <div>
                  <div style={{ fontWeight: 'bold', color: '#1976d2' }}>
                    Linked to Client:{' '}
                    {linkedClient.type === 'individual' ? linkedClient.name : linkedClient.retailerName}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#555' }}>
                    {linkedClient.email && `${linkedClient.email} • `}
                    {linkedClient.phone && `${linkedClient.phone} • `}
                    Client ID: {linkedClient.id.substring(0, 8)}...
                  </div>
                </div>
              </div>
            )}

            {/* Customer Info */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px',
                marginBottom: '20px'
              }}
            >
              <div>
                <label
                  style={{ display: 'flex', fontWeight: 'bold', marginBottom: '4px', alignItems: 'center', gap: '8px' }}
                >
                  Customer Name
                  {renderCustomerTypeBadge()}
                </label>
                <input
                  type="text"
                  value={order.customerName}
                  onChange={e => handleFieldChange('customerName', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Email</label>
                <input
                  type="email"
                  value={order.email}
                  onChange={e => handleFieldChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Phone</label>
                <input
                  type="tel"
                  value={order.phone}
                  onChange={e => handleFieldChange('phone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Order Date</label>
                <input
                  type="date"
                  value={order.orderDate}
                  onChange={e => handleFieldChange('orderDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Street Address</label>
                <input
                  type="text"
                  value={order.streetAddress}
                  onChange={e => handleFieldChange('streetAddress', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>City</label>
                <input
                  type="text"
                  value={order.city}
                  onChange={e => handleFieldChange('city', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Province</label>
                <input
                  type="text"
                  value={order.province}
                  onChange={e => handleFieldChange('province', e.target.value)}
                  placeholder="e.g., ON"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
            </div>

            {/* Order Items */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', fontSize: '1.1rem' }}>
                Order Items
              </label>

              <div style={{ backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '4px' }}>
                {items.length === 0 ? (
                  <p style={{ color: '#999', textAlign: 'center', margin: '20px 0' }}>
                    No items yet. Click "+ Add Item" to add recipes to this order.
                  </p>
                ) : (
                  items.map((item, index) => (
                    <OrderItemRow
                      key={item.id}
                      item={item}
                      recipes={recipes}
                      onChange={updated => handleItemChange(index, updated)}
                      onRemove={() => handleRemoveItem(index)}
                    />
                  ))
                )}

                <button
                  onClick={handleAddItem}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  + Add Item
                </button>
              </div>
            </div>

            {/* Pricing Breakdown */}
            <PricingBreakdown order={order} />

            {/* Original Order Text (for email-imported orders) */}
            {order.originalOrderText && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#666' }}>Original Order Text</h4>
                <textarea
                  readOnly
                  value={order.originalOrderText}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    color: '#555',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    minHeight: '150px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    resize: 'vertical'
                  }}
                />
              </div>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div
                style={{
                  backgroundColor: '#ffebee',
                  border: '1px solid #f44336',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '16px'
                }}
              >
                <strong style={{ color: '#d32f2f', display: 'block', marginBottom: '8px' }}>
                  Please fix the following errors:
                </strong>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#c62828' }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                borderTop: '1px solid #ddd',
                paddingTop: '16px'
              }}
            >
              <div>
                {!order.isFinalized && (
                  <button
                    onClick={handleFinalizeOrder}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Finalize Order
                  </button>
                )}
                {order.isFinalized && (
                  <span
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#E8F5E9',
                      color: '#2E7D32',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      display: 'inline-block'
                    }}
                  >
                    ✓ Order Finalized
                  </span>
                )}
              </div>
              <button
                onClick={() => onDelete(order.id)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Delete Order
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

OrderCardComponent.displayName = 'OrderCard';

export const OrderCard = OrderCardComponent;
